import { forwardRef, useImperativeHandle, useRef } from "react";
import { ScannerHtml5Qrcode, type ScannerHtml5QrcodeRef } from "./ScannerHtml5Qrcode";
import { ScannerJsqrPreprocess, type ScannerJsqrPreprocessRef } from "./ScannerJsqrPreprocess";
import { ScannerQrScanner, type ScannerQrScannerRef } from "./ScannerQrScanner";
import { ScannerZxing, type ScannerZxingRef } from "./ScannerZxing";

export type ScannerType = "qr-scanner" | "html5-qrcode" | "zxing" | "jsqr-preprocess";

export type ScannerMultiProps = {
	onResult: (result: { data: string }) => void;
	className?: string;
	scanner: ScannerType;
	onScannerChange: (scanner: ScannerType) => void;
};

type ActiveScannerRef = ScannerQrScannerRef | ScannerHtml5QrcodeRef | ScannerZxingRef | ScannerJsqrPreprocessRef;

export type ScannerMultiRef = {
	switchCamera: () => Promise<void>;
	hasMultipleCameras: () => Promise<boolean>;
	toggleTorch: () => Promise<void>;
	setScanner: (scanner: ScannerType) => void;
	getCurrentScanner: () => ScannerType;
};

export const SCANNER_ORDER: ScannerType[] = ["qr-scanner", "html5-qrcode", "zxing", "jsqr-preprocess"];

const SCANNER_NAMES: Record<ScannerType, string> = {
	"qr-scanner": "QR Scanner (Enhanced)",
	"html5-qrcode": "HTML5 QRCode",
	"zxing": "ZXing",
	"jsqr-preprocess": "jsQR + Preprocessing",
};

export const getScannerName = (type: ScannerType): string => SCANNER_NAMES[type];

export const ScannerMulti = forwardRef<ScannerMultiRef, ScannerMultiProps>((props, forwardedRef) => {
	const { onResult, className, scanner, onScannerChange } = props;
	const activeScannerRef = useRef<ActiveScannerRef>(null);

	useImperativeHandle(forwardedRef, () => ({
		switchCamera: async () => {
			await activeScannerRef.current?.switchCamera();
		},
		hasMultipleCameras: async () => {
			return activeScannerRef.current?.hasMultipleCameras() ?? Promise.resolve(false);
		},
		toggleTorch: async () => {
			await activeScannerRef.current?.toggleTorch();
		},
		setScanner: (s: ScannerType) => {
			onScannerChange(s);
		},
		getCurrentScanner: () => scanner,
	}));

	return (
		<>
			{scanner === "qr-scanner" && <ScannerQrScanner ref={activeScannerRef} onResult={onResult} className={className} />}
			{scanner === "html5-qrcode" && <ScannerHtml5Qrcode ref={activeScannerRef} onResult={onResult} className={className} />}
			{scanner === "zxing" && <ScannerZxing ref={activeScannerRef} onResult={onResult} className={className} />}
			{scanner === "jsqr-preprocess" && <ScannerJsqrPreprocess ref={activeScannerRef} onResult={onResult} className={className} />}
		</>
	);
});
